import React, { useState, useEffect } from "react";
import "../../styles/staff.css";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlined from "@mui/icons-material/EditOutlined";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import DeleteConfirmationModal from "../deleteconfirmationmodal";
import { API_BASE_URL } from "../../Config/config";

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newStaff, setNewStaff] = useState({
    staff_name: "",
    role: "",
    email: "",
    phone: "",
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // Add safety check here to prevent the error
  const currentItems =
    filteredStaff?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((filteredStaff?.length || 0) / itemsPerPage);

  const fetchStaff = () => {
    axios
      .get(`${API_BASE_URL}/staff`)
      .then((response) => {
        // safety checks for the response data
        const staffData = response.data?.staff || [];
        // console.log("API Response:", response.data);
        // console.log("Staff Data:", staffData);

        // staffData is an array
        const safeStaffData = Array.isArray(staffData) ? staffData : [];

        setStaff(safeStaffData);
        setFilteredStaff(safeStaffData);
      })
      .catch((error) => {
        // console.error("Error fetching staff data:", error);
        setStaff([]);
        setFilteredStaff([]);
        toast.error("Failed to fetch staff data");
      });
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    if (query === "") {
      setFilteredStaff(staff);
    } else {
      // Add safety check for staff array
      const safeStaff = Array.isArray(staff) ? staff : [];
      const filtered = safeStaff.filter(
        (staffMember) =>
          staffMember?.staff_id?.toLowerCase().includes(query.toLowerCase()) ||
          staffMember?.staff_name?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredStaff(filtered);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStaff({ ...newStaff, [name]: value });
  };

  // Function for adding a new staff member
  const handleAddStaff = () => {
    if (
      newStaff.staff_name &&
      newStaff.role &&
      newStaff.email &&
      newStaff.phone
    ) {
      setLoading(true);

      axios
        .post(`${API_BASE_URL}/staff/add-staff`, newStaff)
        .then((response) => {
          setLoading(false);
          const newStaffList = [...staff, response.data];
          setStaff(newStaffList);
          setFilteredStaff(newStaffList);
          resetForm();
          toast.success("Staff added successfully");
          fetchStaff();
        })
        .catch((error) => {
          setLoading(false);
          // console.error("Error adding staff member:", error);
          toast.error("Failed to add staff. Please try again.");
        });
    } else {
      toast.error("Please fill in all fields");
    }
  };

  // Function for editing an existing staff member
  const handleEditStaff = async (staffId) => {
    const staffToEdit = staff.find((s) => s.staff_id === staffId);

    if (staffToEdit) {
      setNewStaff(staffToEdit);
      setEditingIndex(staffId);
      setShowModal(true);
    } else {
      toast.error("Staff not found");
    }
  };

  // Function to update the staff details
  const handleUpdateStaff = () => {
    if (
      newStaff.staff_name &&
      newStaff.role &&
      newStaff.email &&
      newStaff.phone
    ) {
      setLoading(true);

      axios
        .put(`${API_BASE_URL}/staff/update/${newStaff.staff_id}`, newStaff)
        .then(() => {
          setLoading(false);
          const updatedStaff = staff.map((s) =>
            s.staff_id === newStaff.staff_id ? newStaff : s
          );
          setStaff(updatedStaff);
          setFilteredStaff(updatedStaff);
          resetForm();
          setShowModal(false);
          toast.success("Staff updated successfully");
        })
        .catch((error) => {
          setLoading(false);
          // console.error("Error updating staff member:", error);
          toast.error("Failed to update staff. Please try again.");
        });
    } else {
      toast.error("Please fill in all fields");
    }
  };

  // Delete staff member
  const handleDeleteStaff = (staffId) => {
    setDeleteLoading(true);
    axios
      .delete(`${API_BASE_URL}/staff/delete/${staffId}`)
      .then(() => {
        const updatedStaff = staff.filter((s) => s.staff_id !== staffId);
        setStaff(updatedStaff);
        setFilteredStaff(updatedStaff);
        setShowDeleteModal(false);
        setStaffToDelete(null);
        setDeleteLoading(false);

        toast.success("Staff deleted successfully", {
          autoClose: 2000,
          onClose: () => {
            fetchStaff();
          },
        });
      })
      .catch((error) => {
        // console.error("Error deleting staff member:", error);
        toast.error("Failed to delete staff. Please try again.");
        setDeleteLoading(false);
      });
  };

  const handleDeleteClick = (staffMember) => {
    setStaffToDelete(staffMember);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (staffToDelete) {
      handleDeleteStaff(staffToDelete.staff_id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setStaffToDelete(null);
    setDeleteLoading(false);
  };

  const resetForm = () => {
    setNewStaff({
      staff_name: "",
      role: "",
      email: "",
      phone: "",
    });
    setEditingIndex(null);
    setShowModal(false);
  };

  const handleDownloadAll = () => {
    // Add safety check for filteredStaff
    const safeFilteredStaff = Array.isArray(filteredStaff) ? filteredStaff : [];

    if (safeFilteredStaff.length === 0) {
      toast.warning("No staff data to download");
      return;
    }

    const csvData = safeFilteredStaff
      .map(
        ({ staff_id, staff_name, role, email, phone }) =>
          `${staff_id || ""},${staff_name || ""},${role || ""},${email || ""},${
            phone || ""
          }`
      )
      .join("\n");
    const blob = new Blob(
      [`Staff ID,Staff Name,Role,Email,Phone Number\n${csvData}`],
      { type: "text/csv" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "staff_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="staff-container">
      <h1
        style={{
          fontSize: "30px",
          marginBottom: "20px",
          fontWeight: "bold",
          color: "#000000",
        }}
      >
        Staff
      </h1>
      <ToastContainer />
      <div className="staff-header">
        <input
          type="text"
          placeholder="search staff..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-box"
        />
        <button className="add-staff-btn" onClick={() => setShowModal(true)}>
          Add Staff
        </button>
        <button className="download-btn" onClick={handleDownloadAll}>
          Download All
        </button>
      </div>

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>
            {editingIndex !== null
              ? "Updating staff member..."
              : "Adding staff member..."}
          </p>
        </div>
      )}

      <table className="staff-table">
        <thead>
          <tr>
            <th>Staff ID</th>
            <th>Staff Name</th>
            <th>Role</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map((staffMember, index) => (
              <tr key={staffMember.staff_id || index}>
                <td>{staffMember.staff_id}</td>
                <td>{staffMember.staff_name}</td>
                <td>{staffMember.role}</td>
                <td>{staffMember.email}</td>
                <td>{staffMember.phone}</td>
                <td>
                  <button
                    onClick={() => handleEditStaff(staffMember.staff_id)}
                    className="edit-btn"
                    title="Edit Staff"
                  >
                    <EditOutlined />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(staffMember)}
                    className="delete-btn"
                    title="Delete Staff"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="no-staff-msg">
                No staff found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="pagination-container">
        <div className="pagination">
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="pagination-button"
            title="First Page"
          >
            &laquo;
          </button>
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
            title="Previous Page"
          >
            &lsaquo;
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => paginate(pageNum)}
                className={`pagination-button ${
                  currentPage === pageNum ? "active" : ""
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
            title="Next Page"
          >
            &rsaquo;
          </button>
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-button"
            title="Last Page"
          >
            &raquo;
          </button>
        </div>

        <div className="items-per-page-info">
          Showing {indexOfFirstItem + 1}-
          {Math.min(indexOfLastItem, filteredStaff.length || 0)} of{" "}
          {filteredStaff.length || 0} items
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {editingIndex !== null ? "Edit Staff Details" : "Add New Staff"}
            </h3>

            {/* Staff Name Input */}
            <input
              type="text"
              name="staff_name"
              placeholder="Staff Name"
              value={newStaff.staff_name}
              onChange={handleInputChange}
              required
            />

            {/* Staff Role Input */}
            <input
              type="text"
              name="role"
              placeholder="Staff Role"
              value={newStaff.role}
              onChange={handleInputChange}
              required
            />

            {/* Email Input */}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={newStaff.email}
              onChange={handleInputChange}
              required
            />

            {/* Phone Number Input */}
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={newStaff.phone}
              onChange={handleInputChange}
              required
            />

            {/* Modal Action Buttons */}
            <div className="modal-actions">
              <button
                onClick={
                  editingIndex !== null ? handleUpdateStaff : handleAddStaff
                }
                className="add-btn"
                disabled={loading}
              >
                {loading
                  ? editingIndex !== null
                    ? "Updating..."
                    : "Adding..."
                  : editingIndex !== null
                  ? "Update Staff"
                  : "Add Staff"}
              </button>

              {/* Cancel Button */}
              <button
                onClick={resetForm}
                className="cancel-btn"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Staff Member"
        message="Are you sure you want to delete this staff member"
        itemName={staffToDelete?.staff_name}
        confirmText="Delete Staff"
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default Staff;
